/**
 * Переводы админ-раздела «Мессенджеры» (DEV-1712, макеты MSG-14 и MSG-03).
 * Подмешиваются в базовые локали через deepMerge.
 *
 * Неймспейс `messengerAdmin`: ветка `messenger.telegram` описывает личный
 * аккаунт менеджера, и складывать туда общий аккаунт компании значило бы
 * однажды перепутать, чью сессию показывает экран.
 */
type LocaleOverride = Record<string, unknown>

type Locale = 'ru' | 'en' | 'vn'

const build = (l: Locale): LocaleOverride => ({
  common: {
    navigation: {
      adminMessengers: { ru: 'Мессенджеры', en: 'Messengers', vn: 'Trình nhắn tin' }[l],
    },
  },
  messengerAdmin: {
    title: { ru: 'Мессенджеры', en: 'Messengers', vn: 'Trình nhắn tin' }[l],
    // Подписи кнопок общие для всех карточек: WhatsApp и Zalo — не Telegram.
    actions: {
      connect: { ru: 'Подключить', en: 'Connect', vn: 'Kết nối' }[l],
      configure: { ru: 'Настроить', en: 'Configure', vn: 'Cấu hình' }[l],
    },
    noAccess: {
      badge: { ru: 'Нет доступа', en: 'No access', vn: 'Không có quyền' }[l],
      hint: {
        ru: 'Нужно право messengers:manage (admin / hos)',
        en: 'Requires messengers:manage (admin / hos)',
        vn: 'Cần quyền messengers:manage (admin / hos)',
      }[l],
    },
    telegram: {
      title: 'Telegram Support',
      badge: {
        connected: {
          ru: 'Подключён · {account}',
          en: 'Connected · {account}',
          vn: 'Đã kết nối · {account}',
        }[l],
        connectedPlain: { ru: 'Подключён', en: 'Connected', vn: 'Đã kết nối' }[l],
        notConnected: { ru: 'Не подключён', en: 'Not connected', vn: 'Chưa kết nối' }[l],
        pendingQr: { ru: 'Ожидание входа', en: 'Waiting for login', vn: 'Đang chờ đăng nhập' }[l],
        passwordNeeded: {
          ru: 'Нужен пароль 2FA',
          en: '2FA password required',
          vn: 'Cần mật khẩu 2FA',
        }[l],
        revoked: { ru: 'Сессия отозвана', en: 'Session revoked', vn: 'Phiên đã bị thu hồi' }[l],
        error: { ru: 'Ошибка', en: 'Error', vn: 'Lỗi' }[l],
        unknown: { ru: 'Состояние неизвестно', en: 'State unknown', vn: 'Không rõ trạng thái' }[l],
      },
      connected: {
        phone: {
          ru: 'Аккаунт поддержки: {phone}',
          en: 'Support account: {phone}',
          vn: 'Tài khoản hỗ trợ: {phone}',
        }[l],
        // В макете здесь «входящие → автолид», но автолид — это DEV-1830, и её
        // ещё нет: обещать работающую функцию карточка не должна.
        session: {
          ru: 'Сессия активна · вход по QR с 2FA',
          en: 'Session active · QR login with 2FA',
          vn: 'Phiên đang hoạt động · đăng nhập QR kèm 2FA',
        }[l],
        visibility: {
          ru: 'Чаты видят admin / hos · звонки выключены',
          en: 'Chats visible to admin / hos · calls disabled',
          vn: 'Chat hiển thị cho admin / hos · cuộc gọi đã tắt',
        }[l],
      },
      empty: {
        notLinked: {
          ru: 'Аккаунт поддержки не привязан',
          en: 'Support account is not linked',
          vn: 'Chưa liên kết tài khoản hỗ trợ',
        }[l],
        howTo: {
          ru: 'Подключение: QR-логин, как у менеджера',
          en: 'Connection: QR login, same as for a manager',
          vn: 'Kết nối: đăng nhập QR như của quản lý',
        }[l],
        // Эту ветку видит только тот, у кого право есть: про нехватку права
        // ему говорить нечего, а вот кто вправе подключать — полезно.
        whoCanConnect: {
          ru: 'Подключить может admin или hos',
          en: 'Admin or hos can connect it',
          vn: 'Admin hoặc hos có thể kết nối',
        }[l],
      },
      // Имя и телефон переживают отключение: бэкенд при Logout чистит только
      // сессию и статус. Про привязку такая строка ничего не утверждает.
      lastLogin: {
        ru: 'Последний вход был под {account}',
        en: 'The last sign-in was as {account}',
        vn: 'Lần đăng nhập gần nhất là {account}',
      }[l],
      // Вход начат, но не завершён: тело карточки говорит то же, что бейдж.
      pending: {
        qr: {
          ru: 'Вход начат: ожидается скан QR-кода',
          en: 'Login started: waiting for the QR scan',
          vn: 'Đã bắt đầu đăng nhập: đang chờ quét mã QR',
        }[l],
        password: {
          ru: 'Вход начат: нужен пароль 2FA',
          en: 'Login started: the 2FA password is required',
          vn: 'Đã bắt đầu đăng nhập: cần mật khẩu 2FA',
        }[l],
        // К брошенному QR вернуться нельзя: сервер выдаст новый код.
        hint: {
          ru: 'Аккаунт ещё не подключён — начните вход заново',
          en: 'The account is not connected yet — start the login over',
          vn: 'Tài khoản chưa được kết nối — hãy bắt đầu đăng nhập lại',
        }[l],
        // Шаг пароля общего аккаунта продолжается без нового QR.
        resumeHint: {
          ru: 'Сервер ждёт пароль — вернитесь к его вводу, новый QR не нужен',
          en: 'The server is waiting for the password — go back to it, no new QR needed',
          vn: 'Máy chủ đang chờ mật khẩu — hãy quay lại nhập, không cần mã QR mới',
        }[l],
      },
      // Статус error приходит и после неудачной первой попытки, когда аккаунт
      // ни разу не авторизовался: привязку эта ветка не утверждает.
      failed: {
        error: {
          ru: 'Последняя попытка подключения завершилась ошибкой',
          en: 'The last connection attempt ended with an error',
          vn: 'Lần kết nối gần nhất đã kết thúc do lỗi',
        }[l],
        // Про привязку статус error не говорит ничего — и мы тоже не говорим.
        hint: {
          ru: 'Подключение не работает — начните вход заново',
          en: 'The connection is down — start the login over',
          vn: 'Kết nối không hoạt động — hãy bắt đầu đăng nhập lại',
        }[l],
      },
      // Статус не прочитан: карточка не знает состояния и не выдумывает его.
      unknown: {
        hint: {
          ru: 'Аккаунт мог остаться подключённым — проверьте после повторной попытки',
          en: 'The account may still be connected — check again after retrying',
          vn: 'Tài khoản có thể vẫn đang kết nối — hãy kiểm tra lại sau khi thử lại',
        }[l],
      },
      // Отзыв — конец живой сессии, то есть аккаунт был и остаётся привязан.
      relogin: {
        revoked: {
          ru: 'Сессия аккаунта поддержки отозвана',
          en: 'The support account session was revoked',
          vn: 'Phiên của tài khoản hỗ trợ đã bị thu hồi',
        }[l],
        hint: {
          ru: 'Аккаунт привязан — нужен повторный вход по QR',
          en: 'The account stays linked — sign in again via QR',
          vn: 'Tài khoản vẫn được liên kết — hãy đăng nhập lại bằng QR',
        }[l],
      },
      actions: {
        change: { ru: 'Сменить аккаунт', en: 'Change account', vn: 'Đổi tài khoản' }[l],
        disconnect: { ru: 'Отключить аккаунт', en: 'Disconnect account', vn: 'Ngắt tài khoản' }[l],
        relogin: { ru: 'Войти заново', en: 'Sign in again', vn: 'Đăng nhập lại' }[l],
        finishLogin: {
          ru: 'Завершить вход',
          en: 'Finish login',
          vn: 'Hoàn tất đăng nhập',
        }[l],
        // Продолжить брошенную попытку нечем — обещаем ровно то, что будет.
        restart: { ru: 'Начать заново', en: 'Start over', vn: 'Bắt đầu lại' }[l],
      },
      confirmDisconnect: {
        ru: 'Точно отключить?',
        en: 'Disconnect for sure?',
        vn: 'Chắc chắn ngắt?',
      }[l],
      // Смена аккаунта тоже гасит живую сессию — спрашиваем так же, как при отключении.
      confirmChange: {
        ru: 'Сменить аккаунт? Текущий будет отключён',
        en: 'Change the account? The current one will be disconnected',
        vn: 'Đổi tài khoản? Tài khoản hiện tại sẽ bị ngắt',
      }[l],
    },
    login: {
      title: {
        ru: 'Общий аккаунт Telegram',
        en: 'Shared Telegram account',
        vn: 'Tài khoản Telegram chung',
      }[l],
      steps: {
        qr: { ru: 'ШАГ 1 · QR', en: 'STEP 1 · QR', vn: 'BƯỚC 1 · QR' }[l],
        twoFa: { ru: 'ШАГ 2 · 2FA', en: 'STEP 2 · 2FA', vn: 'BƯỚC 2 · 2FA' }[l],
        done: { ru: 'ГОТОВО', en: 'DONE', vn: 'XONG' }[l],
      },
      qrTitle: { ru: 'Отсканируйте QR-код', en: 'Scan the QR code', vn: 'Quét mã QR' }[l],
      qrHint: {
        ru: 'Telegram на телефоне → Настройки → Устройства → Подключить устройство. Код обновляется автоматически.',
        en: 'Telegram on your phone → Settings → Devices → Link Device. The code refreshes automatically.',
        vn: 'Telegram trên điện thoại → Cài đặt → Thiết bị → Liên kết thiết bị. Mã tự làm mới.',
      }[l],
      expiresIn: {
        ru: 'Обновится через {time}',
        en: 'Refreshes in {time}',
        vn: 'Làm mới sau {time}',
      }[l],
      twoFaTitle: {
        ru: 'Пароль двухфакторной защиты',
        en: 'Two-factor password',
        vn: 'Mật khẩu bảo vệ hai lớp',
      }[l],
      twoFaHint: {
        ru: 'На аккаунте включена облачная 2FA. Введите пароль, чтобы завершить вход.',
        en: 'Cloud 2FA is enabled on the account. Enter the password to finish signing in.',
        vn: 'Tài khoản đang bật 2FA đám mây. Nhập mật khẩu để hoàn tất đăng nhập.',
      }[l],
      submit: { ru: 'Подтвердить', en: 'Confirm', vn: 'Xác nhận' }[l],
      wrongPassword: {
        ru: 'Неверный пароль — попробуйте ещё раз',
        en: 'Wrong password — try again',
        vn: 'Sai mật khẩu — hãy thử lại',
      }[l],
      doneTitle: {
        ru: 'Аккаунт подключён',
        en: 'Account connected',
        vn: 'Đã kết nối tài khoản',
      }[l],
      sessionActive: {
        ru: '{phone} · сессия активна',
        en: '{phone} · session active',
        vn: '{phone} · phiên đang hoạt động',
      }[l],
      // Телефон приезжает только со статусом: до него показываем строку без него.
      sessionActivePlain: {
        ru: 'Сессия активна',
        en: 'Session active',
        vn: 'Phiên đang hoạt động',
      }[l],
      // Макет обещал «личные аккаунты отключены» и звонки с общего аккаунта —
      // и то и другое неверно (DEV-1711): личные работают, звонки выключены.
      doneNote: {
        ru: 'Чаты общего аккаунта видны администраторам и руководителям. Личные аккаунты менеджеров работают как раньше, звонки у общего аккаунта выключены.',
        en: 'Chats of the shared account are visible to admins and heads of sales. Managers keep their own accounts as before, and calls are disabled for the shared account.',
        vn: 'Chat của tài khoản chung hiển thị cho quản trị viên và trưởng phòng. Tài khoản riêng của quản lý vẫn hoạt động như trước, cuộc gọi của tài khoản chung bị tắt.',
      }[l],
    },
    soon: {
      badge: {
        ru: 'Скоро · Мессенджеры v2',
        en: 'Soon · Messengers v2',
        vn: 'Sắp có · Trình nhắn tin v2',
      }[l],
      whatsapp: {
        title: 'WhatsApp Business',
        description: {
          ru: 'Официальный Cloud API (WABA)',
          en: 'Official Cloud API (WABA)',
          vn: 'Cloud API chính thức (WABA)',
        }[l],
        ticket: {
          ru: 'Подключение — DEV-1681',
          en: 'Connection — DEV-1681',
          vn: 'Kết nối — DEV-1681',
        }[l],
      },
    },
    // DEV-1681: OA компании подключает администратор — вход в Zalo и разрешение доступа.
    zalo: {
      title: 'Zalo OA',
      badge: {
        connected: { ru: 'Подключён', en: 'Connected', vn: 'Đã kết nối' }[l],
        notConnected: { ru: 'Не подключён', en: 'Not connected', vn: 'Chưa kết nối' }[l],
        unknown: { ru: 'Состояние неизвестно', en: 'State unknown', vn: 'Không rõ trạng thái' }[l],
        disabled: {
          ru: 'Выключен на сервере',
          en: 'Off on the server',
          vn: 'Đã tắt trên máy chủ',
        }[l],
      },
      connected: {
        oa: { ru: 'OA компании: {name}', en: 'Company OA: {name}', vn: 'OA của công ty: {name}' }[
          l
        ],
        routing: {
          ru: 'Входящие получает ответственный за клиента. Zalo-чат клиента видит тот, кому видна его карточка.',
          en: "Incoming messages go to the client's manager. A client's Zalo chat is visible to whoever can see the client card.",
          vn: 'Tin nhắn đến được chuyển cho người phụ trách khách hàng. Ai xem được thẻ khách hàng thì xem được chat Zalo của khách.',
        }[l],
      },
      empty: {
        notConnected: {
          ru: 'OA компании не подключён',
          en: 'The company OA is not connected',
          vn: 'OA của công ty chưa được kết nối',
        }[l],
        howTo: {
          ru: 'Нажмите «Подключить», войдите в Zalo аккаунтом администратора OA и разрешите доступ.',
          en: 'Click "Connect", sign in to Zalo with an OA admin account and allow access.',
          vn: 'Nhấn "Kết nối", đăng nhập Zalo bằng tài khoản quản trị viên OA và cho phép truy cập.',
        }[l],
      },
      disabled: {
        ru: 'Zalo на сервере выключен — подключать пока нечего. Включить его могут разработчики.',
        en: 'Zalo is turned off on the server, so there is nothing to connect yet. Developers can turn it on.',
        vn: 'Zalo đang tắt trên máy chủ nên chưa thể kết nối. Đội phát triển có thể bật.',
      }[l],
      statusFailed: {
        ru: 'Не удалось получить состояние Zalo OA',
        en: 'Could not get the Zalo OA state',
        vn: 'Không lấy được trạng thái Zalo OA',
      }[l],
      actions: {
        disconnect: { ru: 'Отключить', en: 'Disconnect', vn: 'Ngắt kết nối' }[l],
      },
      confirmDisconnect: {
        ru: 'Отключить OA? Сообщения из Zalo перестанут приходить в CRM.',
        en: 'Disconnect the OA? Zalo messages will stop reaching the CRM.',
        vn: 'Ngắt kết nối OA? Tin nhắn Zalo sẽ không còn đến CRM.',
      }[l],
      toast: {
        connected: { ru: 'Zalo OA подключён', en: 'Zalo OA connected', vn: 'Đã kết nối Zalo OA' }[
          l
        ],
        disconnected: {
          ru: 'Zalo OA отключён',
          en: 'Zalo OA disconnected',
          vn: 'Đã ngắt kết nối Zalo OA',
        }[l],
      },
      errors: {
        start: {
          ru: 'Не удалось начать подключение. Повторите.',
          en: 'Could not start connecting. Try again.',
          vn: 'Không thể bắt đầu kết nối. Hãy thử lại.',
        }[l],
        state: {
          ru: 'Ответ Zalo не относится к этому подключению. Нажмите «Подключить» ещё раз.',
          en: 'The Zalo response does not belong to this connection attempt. Click "Connect" again.',
          vn: 'Phản hồi của Zalo không thuộc lần kết nối này. Hãy nhấn "Kết nối" lại.',
        }[l],
        cancelled: {
          ru: 'Доступ в Zalo не выдан — OA не подключён.',
          en: 'Access was not granted in Zalo, so the OA is not connected.',
          vn: 'Chưa cấp quyền trên Zalo nên OA chưa được kết nối.',
        }[l],
        connect: {
          ru: 'Не удалось подключить OA. Нажмите «Подключить» ещё раз.',
          en: 'Could not connect the OA. Click "Connect" again.',
          vn: 'Không thể kết nối OA. Hãy nhấn "Kết nối" lại.',
        }[l],
        disconnect: {
          ru: 'Не удалось отключить OA. Повторите.',
          en: 'Could not disconnect the OA. Try again.',
          vn: 'Không thể ngắt kết nối OA. Hãy thử lại.',
        }[l],
      },
    },
  },
})

export const messengerAdminMessages = {
  ru: build('ru'),
  en: build('en'),
  vn: build('vn'),
}
